import { type SubscriptionAddressFullFragment } from "#graphql/supergraphTypes";
import { toGlobalId } from "#utils/graphql_utils";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { AddressInputType } from "../../../SharedAddressInput/constants";
import { DetailedCheckPaymentPrefObjectType } from "../../common/types";
import { type SelectedBankCountry } from "../../Context/types";
import {
  type AvailablePaymentRailsResponse,
  type PositionCheckSupport,
} from "../../hooks/useAvailablePaymentRails";
import { getIdFromCountry, getNameFromCountry, mapCountryToValueSafely } from "../../mappers";
import { CheckPaymentPrefInputFields, type CheckPaymentPrefInputType } from "./constants";

export const mapFormikAddressToGQLObject = (
  address: AddressInputType,
): SubscriptionAddressFullFragment => {
  const isUSACountry = isUSA(getNameFromCountry(address?.country));

  return {
    city: address?.city?.trim() || null,
    country: address?.country
      ? {
          id: getIdFromCountry(address?.country),
          name: getNameFromCountry(address?.country),
          alpha2code: null,
          code: null,
        }
      : null,
    postalCode: address?.zip?.trim() || null,
    region: !isUSACountry ? address?.province?.trim() || null : null,
    state: isUSACountry
      ? {
          __typename: "StateObjectType",
          // We are over-loading this object and should narrow it.
          code: address?.state?.trim() ?? "",
          // We are over-loading this object and should narrow it.
          id: address?.state?.trim() ?? "",
          name: "",
        }
      : null,
    street: address?.streetLine1?.trim() || null,
    street2: address?.streetLine2?.trim() || null,
    street3: null,
  };
};

export const getAddressSummary = (address: SubscriptionAddressFullFragment): string => {
  const addressStreet = [address.street, address.street2, address.street3, address.city]
    .filter((a) => Boolean(a))
    .join(" ");

  const addressRegion = [
    address.state?.name,
    address.postalCode,
    address.region,
    address.country?.name,
  ]
    .filter((a) => Boolean(a))
    .join(" ");

  return `${addressStreet}, ${addressRegion}`;
};

export const getBankInfoRedacted = (
  payeeName: string,
  address: SubscriptionAddressFullFragment | null,
): string => {
  return address ? `${payeeName}; ${getAddressSummary(address)}` : payeeName;
};

export const mapFormStateToContextState = (
  values: CheckPaymentPrefInputType,
): DetailedCheckPaymentPrefObjectType => {
  const payeeName = values[CheckPaymentPrefInputFields.PayeeName].trim();

  const address = !values[CheckPaymentPrefInputFields.Address].sameAsMailingAddress
    ? mapFormikAddressToGQLObject(values?.address)
    : null;

  return {
    __typename: "CheckPaymentPrefObjectType",
    id: null,
    address,
    memo: values[CheckPaymentPrefInputFields.Memo]?.trim() ?? "",
    payeeName,
    useAccountMailingAddress:
      values[CheckPaymentPrefInputFields.Address].sameAsMailingAddress ?? false,
    naturalPersonDateOfBirth: null,
    naturalPersonEmailAddress: null,
    naturalPersonFirstName: null,
    naturalPersonLastName: null,
    naturalPersonMiddleName: null,
    paymentMethod: PaymentMethod.Check,
    paymentPrefId: null,
    bankCountry: null,
    bankName: null,
    accountHolderType: null,
    bankInfoRedacted: getBankInfoRedacted(payeeName, address),
  };
};

const mapGQLAddressToFormikState = (
  gqlAddress?: SubscriptionAddressFullFragment | null,
  sameAsMailingAddress?: boolean | null,
  selectedBankCountry?: SelectedBankCountry,
): AddressInputType => {
  const countryName = gqlAddress?.country?.name;

  return {
    sameAsMailingAddress: sameAsMailingAddress ?? false,
    streetLine1: gqlAddress?.street ?? "",
    streetLine2: gqlAddress?.street2 ?? "",
    city: gqlAddress?.city ?? "",
    zip: gqlAddress?.postalCode ?? "",
    state: countryName && isUSA(countryName) ? gqlAddress?.state?.code ?? "" : "",
    province: countryName && !isUSA(countryName) ? gqlAddress?.region ?? "" : "",
    country: gqlAddress?.country?.id
      ? mapCountryToValueSafely(gqlAddress.country.id, countryName)
      : selectedBankCountry?.id
        ? mapCountryToValueSafely(selectedBankCountry.id, selectedBankCountry.name)
        : "",
  };
};

export const mapContextStateToFormState = (
  input?: DetailedCheckPaymentPrefObjectType,
  selectedBankCountry?: SelectedBankCountry,
): CheckPaymentPrefInputType => {
  return {
    address: mapGQLAddressToFormikState(
      input?.address,
      input?.useAccountMailingAddress,
      selectedBankCountry,
    ),
    payeeName: input?.payeeName ?? "",
    memo: input?.memo ?? "",
  };
};

export type MailingCountry =
  | {
      objectId: number | null;
      objectName: string | null;
    }
  | null
  | undefined;

export type MailingCountryEvaluation = {
  isMismatch: boolean;
  commonMailingCountryObjectId: number | null | undefined;
  commonMailingCountryObjectName: string | null | undefined;
};

/**
 * Evaluates the supplied per-account mailing countries against the bank country picked upstream.
 *
 * A mismatch is reported when:
 *   1. Multiple accounts (LP portal) have differing mailing countries among themselves, OR
 *   2. `contextCountryName` is set and the single distinct mailing country differs from it.
 *
 * Also returns the common mailing country's id/name when every supplied account shares the same
 * country, so callers can hydrate the bank country from the mailing address (FF-7685).
 */
export const evaluateMailingCountryMismatch = (
  countries: MailingCountry[],
  contextCountryName: string | undefined,
): MailingCountryEvaluation => {
  const distinctNames = new Set(
    countries.map((c) => c?.objectName).filter((n): n is string => n != null),
  );
  const firstCountry = countries.find((c) => !!c?.objectName);
  const hasSingleCommonCountry = distinctNames.size === 1;

  let isMismatch = false;
  if (distinctNames.size > 1) {
    isMismatch = true;
  } else if (contextCountryName && hasSingleCommonCountry) {
    const [only] = Array.from(distinctNames);
    isMismatch = only !== contextCountryName;
  }

  return {
    isMismatch,
    commonMailingCountryObjectId: hasSingleCommonCountry ? firstCountry?.objectId : undefined,
    commonMailingCountryObjectName: hasSingleCommonCountry
      ? firstCountry?.objectName
      : undefined,
  };
};

/**
 * Computes the intersection of all Check-rail allowed-country ID sets from the rails response.
 *
 * Returns null when no restrictions are defined (every country is allowed).
 * Returns an empty Set when the intersection of all restrictions yields no shared countries.
 */
export const computeAllowedCheckCountryIds = (
  availablePaymentRailsResponse: AvailablePaymentRailsResponse | undefined,
): Set<string> | null => {
  const checkRail = availablePaymentRailsResponse?.rails?.find((r) => r.railCode === "check");
  const restrictions = (checkRail?.positionCountryRestrictions ?? []) as PositionCheckSupport[];

  const allowedSets: Set<string>[] = [];
  for (const res of restrictions) {
    if (res.allowedCountryIds && res.allowedCountryIds.length) {
      allowedSets.push(new Set<string>(res.allowedCountryIds));
    }
  }

  if (allowedSets.length === 0) return null;

  const [first, ...rest] = allowedSets;
  const intersection = new Set<string>(first);
  for (const next of rest) {
    for (const id of intersection) {
      if (!next.has(id)) intersection.delete(id);
    }
    // No country is shared across positions — no further restriction can shrink it more.
    if (intersection.size === 0) break;
  }

  return intersection;
};

/**
 * Returns a country-dropdown predicate derived from the allowed Check-rail country set.
 * Returns undefined when no restriction applies (every country should be shown).
 */
export const buildCheckCountryFilter = (
  allowedCountryIds: Set<string> | null,
): ((country: { id: string }) => boolean) | undefined => {
  if (!allowedCountryIds) return undefined;
  return (country) => allowedCountryIds.has(country.id);
};

/**
 * Returns true when the mailing country is NOT in the Check rail's allowed-country intersection,
 * meaning the user cannot safely use "Same as mailing address" for this rail.
 *
 * The mailing country's numeric `objectId` is converted into the same Relay global ID format
 * used by `allowedCountryIds`, so the comparison stays purely id-based and avoids the brittle
 * name-match lookup that previously slipped through when country names diverged.
 *
 * Returns false when:
 *  - There are no restrictions (every country is allowed).
 *  - The mailing country id is unknown (we cannot determine a conflict).
 */
export const isMailingCountryOutsideAllowedRail = (
  commonMailingCountryObjectId: number | null | undefined,
  allowedCountryIds: Set<string> | null,
): boolean => {
  if (!allowedCountryIds) return false;
  if (commonMailingCountryObjectId == null) return false;

  const mailingCountryGlobalId = toGlobalId("CountryObjectType", commonMailingCountryObjectId);
  return !allowedCountryIds.has(mailingCountryGlobalId);
};
